/**
 * CAOS-211 — the bundled standard-package catalog.
 *
 * There is no marketplace yet (`/exchange` is M5e-deferred), so the empty-workspace picker
 * discovers installable packages from THIS small, compiled-in manifest of the known standard
 * packages. Every label / key / package-key here is taken verbatim from the authored bodies in
 * the first-party package repositories checked out next to this one (`caos-pkg-<name>`); a
 * change to an authored label should be mirrored here.
 *
 * When the marketplace exists, replace this manifest with a fetch from `/exchange` — the picker
 * consumes an array of {@link CatalogEntry} either way, so nothing downstream changes.
 *
 * Two groups map to the two empty-workspace CTAs:
 *   • `workspace` — the "Install workspace" CTA. ONE entry that installs
 *     `pkg_component_library` (the pieces every screen is assembled from), `pkg_core_shell`
 *     (masters the shell) AND `pkg_setup` (masters Setup): a workspace is all three.
 *   • `app` — the "Deploy first app" CTA. `pkg_sales_atlas` (the featured first app) plus the
 *     injector packages that hang off the shell.
 *
 * EVERY package the roster marks `shipped` has to appear in some entry's `packages`, because
 * this list is the only way anything gets installed — a shipped package missing from here is a
 * package no org can ever hold. `host/test/install.test.ts` fails when the two disagree.
 *
 * ORDER HERE IS A CONVENIENCE, NOT THE GUARANTEE. `pkg_core_shell` and `pkg_setup` DECLARE a
 * dependency on `pkg_component_library`, and `installPackage`'s dependency gate refuses either
 * one until the library is live — so the requirement holds from the API and the command line and
 * a direct registry install exactly as it holds here. Listing the library first simply means the
 * picker satisfies that gate on its first pass instead of failing and retrying. An array position
 * only the picker respects would have been a rule the other three ways in could walk straight
 * past, which is the same shape of hole as a refusal that lives in one surface instead of on the
 * shared write path.
 */

/** Which empty-workspace CTA group an entry belongs to. */
export type PackageKind = 'workspace' | 'app';

/**
 * How an installed app package surfaces inside the shell chrome — the seam it injects into and
 * the source key it appears as. Used by the dev fixture to build a faithful post-install
 * `hand-to-shell` plan (so "the app appears in the switcher" is driven by what was installed,
 * not faked). Absent for the workspace entry (it IS the chrome, not an injection into it).
 */
export interface ShellInjection {
  readonly seam: 'navigation-entries' | 'header-actions' | 'avatar-menu' | 'edge-anchored';
  readonly sourceKey: string;
  /**
   * CAOS-341/450 — for an `edge-anchored` injection, the declared placement: which edge, its
   * DECLARED stacking order among the surfaces sharing that edge, the short word its gutter tab
   * shows, and its identity colour. Mirrors {@link import('../kernel-contract.js').ShellInjectionRef}
   * `anchor` — this is the picker's own copy of the same authored contract, kept here because the
   * picker simulates a fresh install without a kernel round trip.
   */
  readonly anchor?: { readonly edge: 'bottom-right' | 'right'; readonly order?: number; readonly label?: string; readonly colour?: string };
}

/** One installable catalog entry. */
export interface CatalogEntry {
  /** Stable catalog id — the primary package key (also the entry's DOM id in the picker). */
  readonly id: string;
  /** Human label, verbatim from the authored package body. */
  readonly label: string;
  /** One-line description of what installing it does. */
  readonly summary: string;
  /** Which CTA group surfaces this entry. */
  readonly kind: PackageKind;
  /** The ordered package keys to install for this entry (usually one; the workspace is two). */
  readonly packages: readonly string[];
  /** The featured entry in its group — presented first / emphasised. */
  readonly featured?: boolean;
  /** How an app entry surfaces in the shell (drives the dev fixture's post-install frame). */
  readonly shellInjection?: ShellInjection;
  /**
   * CAOS-1728 — for a package that is NOT in the compiled-in standard registry, the universal
   * install key to install it by, keyed by the package key it installs.
   *
   * WHY THIS IS NOT JUST ANOTHER STRING IN `packages`. There are two resolvers and they read
   * different namespaces. `packages install <key>` resolves a key the kernel worker was BUILT
   * with (`api/packages-routes.ts`); `packages install-by-key <pvk_…>` resolves the global
   * published-version registry (`api/package-install-by-key-routes.ts`), which is what
   * `caos package publish` writes to. A `pvk_` is an opaque minted credential-shaped identifier,
   * not a package key, so putting one in `packages` would make `catalogInstallOrder` emit it as
   * though it were installable by the first route and `isInstalled` compare it against live
   * package keys it can never match.
   *
   * Keyed by package key rather than held as one string because an entry may install several
   * packages (the standard workspace installs three) and only some of them may need this door.
   * An entry whose packages are all in the standard registry omits the field entirely, which is
   * every entry in {@link STANDARD_CATALOG} and is why nothing about the customer picker changes.
   */
  readonly universalKeys?: Readonly<Record<string, string>>;
}

/**
 * The standard catalog. Ordered so the featured entry leads its group.
 */
export const STANDARD_CATALOG: readonly CatalogEntry[] = [
  {
    // The entry installs three packages, so its id names the ENTRY rather than any one of them.
    // It was `pkg_core_shell` while core-shell was the whole of a workspace; it is not any more,
    // and an id naming the second of three packages reads as though the other two were an
    // afterthought.
    id: 'workspace',
    label: 'Workspace',
    summary:
      'Your team’s home base for every app: search, navigation, and a menu to move between apps — plus Setup, where admins add people and control who can see what. Set this up first; everything else runs here.',
    kind: 'workspace',
    // The component library goes in FIRST. Every screen the shell and Setup draw is assembled
    // from those pieces, and a page placing a piece the org does not hold is refused at deploy —
    // so the library has to be there before anything places one. The two packages that need it
    // SAY SO (`dependencies` on each of their manifests) and the install gate enforces it; this
    // order just means the gate is satisfied first time through.
    packages: ['pkg_component_library', 'pkg_core_shell', 'pkg_setup'],
    featured: true,
  },
  {
    id: 'pkg_sales_atlas',
    label: 'Sales Atlas',
    summary:
      'Run sales end to end in one place — accounts, contacts, projects and orders, all linked — so nothing slips between a first conversation and a signed order.',
    kind: 'app',
    packages: ['pkg_sales_atlas'],
    featured: true,
    shellInjection: { seam: 'navigation-entries', sourceKey: 'sales_atlas' },
  },
  {
    id: 'pkg_atlantis_ai',
    label: 'Atlantis AI',
    summary:
      'An AI assistant that answers questions, finds records, and drafts and updates your work — just ask in plain language and it acts across your apps and data.',
    kind: 'app',
    packages: ['pkg_atlantis_ai'],
    // CAOS-450/451 — every edge-anchored app declares its own placement (edge/order/label/colour),
    // so the picker's post-install simulation renders the same gutter tab a real install would.
    // These literals are kept in sync with each package's real compiled capabilityClaims by hand;
    // there is no automated check tying them together (see the CAOS-451 review finding).
    shellInjection: {
      seam: 'edge-anchored',
      sourceKey: 'atlantis_ai',
      anchor: { edge: 'right', order: 1, label: 'AI', colour: '#1E7DB3' },
    },
  },
  {
    id: 'pkg_report_a_bug',
    label: 'Report a Bug',
    summary:
      'Let anyone flag a problem in a couple of clicks — a screenshot and the exact screen are captured automatically — so issues reach your team with everything needed to fix them.',
    kind: 'app',
    packages: ['pkg_report_a_bug'],
    shellInjection: {
      seam: 'edge-anchored',
      sourceKey: 'report_a_bug',
      // CAOS-731 — kept in sync by hand with the real compiled capabilityClaims (see the CAOS-451
      // review finding above): report-a-bug now declares its own colour + order too.
      anchor: { edge: 'right', order: 3, label: 'Report', colour: '#B3541E' },
    },
  },
  {
    id: 'pkg_guidance',
    label: 'Guidance Center',
    summary:
      'Built-in help, walkthroughs, and answers to common questions, so your team can get unstuck on their own instead of waiting on support.',
    kind: 'app',
    packages: ['pkg_guidance'],
    // CAOS-148/151 — the Guidance Center surfaces as the always-reachable edge-anchored help tab.
    shellInjection: {
      seam: 'edge-anchored',
      sourceKey: 'guidance_home',
      anchor: { edge: 'right', label: 'Guide' },
    },
  },
  {
    // CAOS-485 — the package was put on the shelf marked `shipped` and offered by nothing, so no
    // org could ever hold it and every assertion about its screens was true of an app nobody could
    // reach. CAOS-578 is the case that says so: "these packages are marked shipped but no catalog
    // entry installs them". A roster row is where a package LIVES; this list is the only way one
    // gets INTO a workspace, as the header of this file says.
    //
    // `navigation-entries`, like Sales Atlas — four tabs reached from the launcher. Not an edge
    // anchor: reading logs is something somebody goes to deliberately, not a gutter tab on every
    // screen. Note Sales Atlas declares no `capabilityClaims` either; this seam is not a claim on
    // a slot, it is what the picker's post-install simulation draws. `sourceKey` is the app's
    // UNPREFIXED key, as it is for `sales_atlas` (namespace `sales`) and `report_a_bug`
    // (namespace `bug`).
    id: 'pkg_logger',
    label: 'Logger',
    summary:
      'Read what your apps recorded as they ran — one request at a time, with every line, change and check that belongs to it gathered together, so a problem somebody reported can be followed end to end.',
    kind: 'app',
    packages: ['pkg_logger'],
    shellInjection: { seam: 'navigation-entries', sourceKey: 'logger' },
  },
  {
    // CAOS-497 — the package was put on the shelf marked `shipped` and offered by nothing, so no
    // org could ever hold it. CAOS-578 is the case that says so: "these packages are marked
    // shipped but no catalog entry installs them". A shelf entry is where a package LIVES; a
    // catalog entry is the only way one gets INTO a workspace, and the two are separate lists on
    // purpose.
    //
    id: 'pkg_developer_tools',
    label: 'Developer Tools',
    summary:
      'The built-in console for people building on this platform — query your data, read one record field by field, inspect an object, send a request to the API, run the tests and read the logs, without leaving your workspace.',
    kind: 'app',
    packages: ['pkg_developer_tools'],
    // `navigation-entries`, not an edge anchor. This console is opened deliberately from the
    // launcher; it does not belong in the gutter of every screen. Sales Atlas is the precedent and
    // the shape is the same — and note it declares no `capabilityClaims` either, because this seam
    // is not a claim on a slot. It is what the picker's post-install simulation draws for an app
    // that simply has tabs. `sourceKey` is the app's UNPREFIXED key, as it is for `sales_atlas`
    // (namespace `sales`) and `report_a_bug` (namespace `bug`).
    shellInjection: { seam: 'navigation-entries', sourceKey: 'developer_tools' },
  },
  {
    id: 'pkg_compliance',
    label: 'Compliance Tools',
    summary:
      'Name a person and see exactly what they can reach — every screen, object and field — against what their role is supposed to have, so access that was granted once and never taken away stops being invisible.',
    kind: 'app',
    packages: ['pkg_compliance'],
    /**
     * CAOS-510 — a NAVIGATION app, not an edge tab, and that follows from what the package
     * declares rather than from a preference. `src/apps/compliance.json` is `navStyle: standard`
     * with a landing tab and four tabs of its own, and its manifest declares
     * `capabilityClaims: []` — so it claims no edge anchor, and an `edge-anchored` entry here
     * would simulate a gutter tab no install produces. It is the same shape as Sales Atlas, which
     * is the other `navStyle: standard` app on the shelf.
     *
     * The `sourceKey` is the app's own `apiName`, which is what the shell routes on
     * (`content-source.ts` matches `a.sourceKey === route.appKey`). Note the Sales Atlas entry
     * above uses `sales_atlas` while that package's app declares `sales`; that predates this and
     * is left alone rather than corrected in passing by a roster change.
     */
    shellInjection: { seam: 'navigation-entries', sourceKey: 'compliance' },
  },
];

/** The catalog entries for a CTA group, featured-first. */
export function catalogForGroup(
  kind: PackageKind,
  catalog: readonly CatalogEntry[] = STANDARD_CATALOG,
): readonly CatalogEntry[] {
  return catalog
    .filter((e) => e.kind === kind)
    .slice()
    .sort((a, b) => Number(b.featured ?? false) - Number(a.featured ?? false));
}

/**
 * CAOS-578 — every package this catalog offers, once each, in an order the dependency gate accepts.
 *
 * The picker installs one ENTRY at a time, because that is how a person chooses. Something that
 * wants the whole shelf — a local run being brought up to a workspace, or a check that drives a
 * screen from sign-in through to its content — wants the same set flattened, and must get it from
 * HERE rather than from a list of its own. A second list would pass while the picker offered
 * something else entirely, which is the exact failure CAOS-578 exists for: a package absent from
 * what the installer offers, with every test still green.
 *
 * Workspace entries lead, for the reason the header gives: the two packages that need the component
 * library declare that dependency and the install gate enforces it, so leading with the workspace
 * means the gate is satisfied on the first pass instead of on a retry.
 */
export function catalogInstallOrder(catalog: readonly CatalogEntry[] = STANDARD_CATALOG): readonly string[] {
  const order: string[] = [];
  for (const kind of ['workspace', 'app'] as const) {
    for (const entry of catalogForGroup(kind, catalog)) {
      for (const key of entry.packages) if (!order.includes(key)) order.push(key);
    }
  }
  return order;
}

/** Look up a single entry by its catalog id. */
export function catalogEntry(
  id: string,
  catalog: readonly CatalogEntry[] = STANDARD_CATALOG,
): CatalogEntry | undefined {
  return catalog.find((e) => e.id === id);
}
